// Gate Pass API service — list student gate passes with server-side filters.
//
// Endpoint: GET /api/student-gatepasses
//   ?school_id=&student_id=&status=&pass_type=&valid_date=&per_page=&page=
//
// NOTE ON ENUMS: Sample responses confirmed `status: "pending"` and
// `pass_type` values "early_departure" and "hostel_outpass". The remaining
// option values below are still best-guesses. These constants are the ONLY
// place to adjust the filter choices — every label/badge on the screen is
// derived from them, and any value the API returns that is *not* listed here is
// still displayed correctly (humanised), so a mismatch only affects which
// choices appear in the dropdowns.

import { apiRequest } from './api';
import { getSchoolId } from './auth';

// ── Filter option constants ──────────────────────────────────

export interface GatePassOption {
    value: string;
    label: string;
}

export const GATE_PASS_STATUS_OPTIONS: GatePassOption[] = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'expired', label: 'Expired' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
];

export const GATE_PASS_TYPE_OPTIONS: GatePassOption[] = [
    { value: 'early_departure', label: 'Early Departure' },
    { value: 'hostel_outpass', label: 'Hostel Outpass' },
    { value: 'medical', label: 'Medical' },
    { value: 'family_emergency', label: 'Family Emergency' },
    { value: 'half_day', label: 'Half Day' },
    { value: 'full_day', label: 'Full Day' },
    { value: 'other', label: 'Other' },
];

// ── Raw API record types ─────────────────────────────────────

/** Parent / guardian linked to the student. The list endpoint sends this as
 *  `null` in the samples seen so far, so the inner field names are unconfirmed
 *  — multiple plausible name/contact keys are accepted defensively. */
export interface GatePassParentRef {
    id?: string;
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    relation?: string | null;
    contact_number?: string | null;
    phone?: string | null;
    phone_number?: string | null;
    mobile?: string | null;
    contact?: string | null;
    email?: string | null;
}

export interface GatePassStudentRef {
    id?: string;
    enrollment_number?: string | null;
    roll_number?: string | null;
    name?: string | null;
    parent?: GatePassParentRef | null;
    /** Fallbacks kept for resilience; the list endpoint sends `name`. */
    first_name?: string | null;
    last_name?: string | null;
    current_class?: { id: string; name: string } | null;
    current_section?: { id: string; name: string } | null;
}

export interface GatePassUserRef {
    id?: string;
    name?: string | null;
}

export interface GatePassSchoolRef {
    id?: string;
    name?: string | null;
}

/** A single gate-pass record. Most fields are optional so the screen stays
 *  resilient to whichever subset the API actually returns. */
export interface GatePassApiRecord {
    id: string;
    organization_id?: string;
    school_id?: string;
    student_id?: string;
    leave_request_id?: string | null;
    pass_type?: string | null;
    status?: string | null;
    reason?: string | null;
    valid_date?: string | null;
    expected_exit_time?: string | null;
    expected_return_time?: string | null;
    actual_exit_time?: string | null;
    actual_return_time?: string | null;
    authorized_person_name?: string | null;
    authorized_person_relation?: string | null;
    authorized_person_contact?: string | null;
    requested_by_user_id?: string | null;
    approved_by_user_id?: string | null;
    scanned_by_guard_user_id?: string | null;
    gatepass_photo_url?: string | null;
    gatepass_photo_thumb_url?: string | null;
    student?: GatePassStudentRef | null;
    school?: GatePassSchoolRef | null;
    requested_by?: GatePassUserRef | null;
    approved_by?: GatePassUserRef | null;
    created_at?: string;
    updated_at?: string;
}

export interface GatePassListMeta {
    current_page: number;
    from?: number;
    last_page: number;
    per_page: number;
    total: number;
}

export interface GatePassListResponse {
    success: boolean;
    message: string;
    data: GatePassApiRecord[];
    meta: GatePassListMeta;
}

export interface FetchGatePassesParams {
    page?: number;
    perPage?: number;
    /** Defaults to the active school from auth when omitted. Pass `null` to skip. */
    schoolId?: string | null;
    studentId?: string;
    status?: string;
    passType?: string;
    /** ISO date string, e.g. "2026-06-01". */
    validDate?: string;
}

// ── API functions ────────────────────────────────────────────

/**
 * List student gate passes with pagination + filters.
 * `school_id` is applied automatically from the logged-in context.
 */
export async function fetchGatePasses(
    params: FetchGatePassesParams = {}
): Promise<GatePassListResponse> {
    const query = new URLSearchParams();

    const schoolId = params.schoolId === undefined ? getSchoolId() : params.schoolId;
    if (schoolId) query.set('school_id', schoolId);
    if (params.studentId) query.set('student_id', params.studentId);
    if (params.status) query.set('status', params.status);
    if (params.passType) query.set('pass_type', params.passType);
    if (params.validDate) query.set('valid_date', params.validDate);
    if (params.perPage) query.set('per_page', String(params.perPage));
    if (params.page) query.set('page', String(params.page));

    const qs = query.toString();
    const res = await apiRequest<GatePassApiRecord[]>(
        `/api/student-gatepasses${qs ? `?${qs}` : ''}`
    );
    // apiRequest returns the full payload, so `meta` is present at runtime.
    return res as unknown as GatePassListResponse;
}

// ── Display helpers ──────────────────────────────────────────

/** Title-case an unknown enum string, e.g. "early_pickup" → "Early Pickup". */
export function humanizeGatePassValue(raw: string | null | undefined): string {
    if (!raw) return '';
    return raw
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function gatePassStatusLabel(status: string | null | undefined): string {
    if (!status) return '—';
    return (
        GATE_PASS_STATUS_OPTIONS.find((o) => o.value === status)?.label ??
        humanizeGatePassValue(status)
    );
}

export function gatePassTypeLabel(passType: string | null | undefined): string {
    if (!passType) return '—';
    return (
        GATE_PASS_TYPE_OPTIONS.find((o) => o.value === passType)?.label ??
        humanizeGatePassValue(passType)
    );
}
